import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import { TranslationService } from '@gv/ammo-grome';
import axios from 'axios';
import { debounce } from 'lodash';

@Component({})
export default class ParameterAutocomplete extends Vue {
  @Inject('translationService') private translationService: () => TranslationService;

  private languages: any = this.$store.getters.languages;
  private currentLanguage = this.$store.getters.currentLanguage;

  @Prop({ required: true }) readonly value: any;
  @Prop({ required: false, type: String, default: '' }) readonly label: string;
  @Prop({ required: false, type: Boolean, default: true }) readonly multiple: boolean;
  @Prop({ required: false, type: Boolean, default: false }) readonly dense: boolean;
  @Prop({ required: false, type: Boolean, default: false }) readonly dark: boolean;
  @Prop({ required: false, type: String, default: 'id' }) readonly itemValue: string;
  @Prop({ required: false, type: String, default: 'name' }) readonly itemText: string;

  public items: Array<any> = [];
  public selectedValues: any = null;
  public loading = false;
  public searchInput: string | null = null;
  public errorMessage = '';
  public salesRepCode: string | null = null;

  private currentPage = 0;
  private pageSize = 50;
  public hasMorePages = true;
  private isLoadingMore = false;
  private lastSearchTerm = '';
  private debouncedSearch: any = null;
  private allLoadedCustomers: Array<any> = []; // Cache of all loaded customers
  public totalCount = 0; // Total count from X-Total-Count header
  private selectAllActive = false; // Track if user clicked Select All

  async created() {
    const currentLanguage = Object.keys(this.languages).includes(navigator.language) ? navigator.language : this.currentLanguage;
    this.translationService().refreshTranslation(currentLanguage);

    // Initialize selected values from prop
    this.selectedValues = this.value || (this.multiple ? [] : null);

    // Create debounced search function
    this.debouncedSearch = debounce((searchTerm: string) => {
      this.searchCustomers(searchTerm);
    }, 300);

    // Check if user is sales rep
    await this.checkSalesRepCode();

    // Load initial page of customers
    await this.loadCustomersPage('', 0);

    // If no initial value, activate Select All mode by default
    if (!this.value || (Array.isArray(this.value) && this.value.length === 0)) {
      this.selectAllActive = true;
      // Select all initially loaded items
      this.$nextTick(() => {
        this.$set(
          this,
          'selectedValues',
          this.items.map(item => item[this.itemValue])
        );
        this.onInput(this.selectedValues);
      });
    }
  }

  /**
   * Vuetify v-intersect handler - called when the append-item becomes visible
   */
  public fetchNextPage(entries: IntersectionObserverEntry[], observer: IntersectionObserver, isIntersecting: boolean): void {
    if (isIntersecting && this.hasMorePages && !this.loading && !this.isLoadingMore) {
      this.loadMore();
    }
  }

  @Watch('value')
  onValueChange(newValue: any) {
    this.selectedValues = newValue || (this.multiple ? [] : null);
  }

  @Watch('searchInput')
  onSearchInputChange(val: string | null) {
    // Use lodash debounced search function
    if (this.debouncedSearch) {
      this.debouncedSearch(val || '');
    }
  }

  /**
   * Computed properties for Select All checkbox
   */
  public get allSelected(): boolean {
    return this.selectedValues.length === this.items.length;
  }

  public get someSelected(): boolean {
    return this.selectedValues.length > 0 && !this.allSelected;
  }

  public get icon(): string {
    if (this.allSelected) return 'mdi-close-box';
    if (this.someSelected) return 'mdi-minus-box';
    return 'mdi-checkbox-blank-outline';
  }

  /**
   * Toggle Select All / Unselect All
   */
  public toggle(): void {
    this.$nextTick(() => {
      if (this.allSelected) {
        // Unselect all
        this.$set(this, 'selectedValues', []);
        this.selectAllActive = false;
      } else {
        // Select all loaded items
        this.$set(
          this,
          'selectedValues',
          this.items.map(item => item[this.itemValue])
        );
        // Mark that Select All is active
        this.selectAllActive = true;
      }
      this.onInput(this.selectedValues);
    });
  }

  /**
   * Check if the current user is a sales representative
   */
  private async checkSalesRepCode(): Promise<void> {
    try {
      const username = this.$store.getters.account.login;
      const response = await axios.get('/fargo/api/user-parameter-values', {
        params: {
          'user.equals': username,
          'description.equals': 'AUSCRMF',
        },
      });

      // Check if response has data and value is not blank
      if (response.data?.length > 0) {
        const value = response.data[0].value;
        if (value && typeof value === 'string' && value.trim().length > 0) {
          this.salesRepCode = value.trim();
        }
      }
    } catch (error) {
      // Proceed without sales rep filter
      this.salesRepCode = null;
    }
  }

  /**
   * Search customers with criteria and conditional sales rep filter
   * Resets pagination and loads first page
   */
  private async searchCustomers(searchTerm: string): Promise<void> {
    this.lastSearchTerm = searchTerm;
    this.currentPage = 0;

    // Preserve currently selected items from the cache
    // This prevents selected items from disappearing when user searches
    const selectedItems = this.allLoadedCustomers.filter(item => this.selectedValues.includes(item[this.itemValue]));

    this.items = selectedItems;

    this.hasMorePages = true;
    await this.loadCustomersPage(searchTerm, 0);
  }

  /**
   * Load a specific page of customers
   */
  private async loadCustomersPage(searchTerm: string, page: number): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      // Build query parameters
      const params: any = {
        'active.equals': 2, // Always include active filter
        page: page,
        size: this.pageSize,
        sort: 'customerName,asc',
      };

      // Add search filter if search term exists
      if (searchTerm && searchTerm.trim().length > 0) {
        params['customerName.contains'] = searchTerm.trim();
      }

      // Conditionally add sales rep filter if user is sales rep
      if (this.salesRepCode) {
        params['salesRepresentative1.equals'] = this.salesRepCode;
      }

      // Make API call to Fargo
      const response = await axios.get('/fargo/api/customers', { params });

      const newItems = response.data || [];

      // Add new items to cache
      const cacheIds = this.allLoadedCustomers.map(item => item[this.itemValue]);
      const newUniqueItems = newItems.filter(item => !cacheIds.includes(item[this.itemValue]));
      this.allLoadedCustomers = [...this.allLoadedCustomers, ...newUniqueItems];

      // Append new items to existing items, avoiding duplicates
      const existingIds = this.items.map(item => item[this.itemValue]);
      const uniqueNewItems = newItems.filter(item => !existingIds.includes(item[this.itemValue]));
      this.items = [...this.items, ...uniqueNewItems];

      // Auto-select new items if Select All is active
      if (this.selectAllActive && uniqueNewItems.length > 0) {
        const newItemIds = uniqueNewItems.map(item => item[this.itemValue]);
        this.$set(this, 'selectedValues', [...this.selectedValues, ...newItemIds]);
        this.onInput(this.selectedValues);
      }

      // Check if there are more pages
      // Spring Data returns X-Total-Count header, check if we have more items
      this.totalCount = parseInt(response.headers['x-total-count'] || '0', 10);
      this.hasMorePages = this.items.length < this.totalCount;

      this.currentPage = page;
    } catch (error) {
      this.errorMessage = 'Error loading customers';
      this.hasMorePages = false;
    } finally {
      this.loading = false;
      this.isLoadingMore = false;
    }
  }

  /**
   * Load more customers (next page) for infinite scroll
   */
  private async loadMore(): Promise<void> {
    if (this.isLoadingMore || !this.hasMorePages || this.loading) {
      return;
    }

    this.isLoadingMore = true;
    await this.loadCustomersPage(this.lastSearchTerm, this.currentPage + 1);
  }

  /**
   * Handle value selection
   */
  public onInput(value: any): void {
    // If user manually deselects items, clear the Select All flag
    if (this.selectAllActive && value.length < this.selectedValues.length) {
      this.selectAllActive = false;
    }

    this.$emit('input', value);
    // Emit selectAll state for parameter system
    this.$emit('select-all-change', this.selectAllActive);
  }

  /**
   * Handle change event (fires after item is selected)
   */
  public onChange(): void {
    // Clear search after item selection
    this.$nextTick(() => {
      this.searchInput = '';
    });
  }
}
